// Pure GTIN / UPC validation helpers. Browser- and SSR-safe.

export type GtinCheck =
  | { ok: true; normalized: string; checkDigit: number }
  | { ok: false; reason: string };

/** GS1 mod-10 check digit for any GTIN-8/12/13/14 input (digits only, no check digit). */
export function computeCheckDigit(digitsWithoutCheck: string): number {
  let sum = 0;
  // Right-to-left: rightmost digit (i = length-1) is weighted ×3, then alternating.
  const n = digitsWithoutCheck.length;
  for (let i = 0; i < n; i++) {
    const d = digitsWithoutCheck.charCodeAt(i) - 48;
    const fromRight = n - 1 - i; // 0-based from right
    const weight = fromRight % 2 === 0 ? 3 : 1;
    sum += d * weight;
  }
  return (10 - (sum % 10)) % 10;
}

/** Validate a UPC-A (12 digits). Returns details or a reason for failure. */
export function validateUpcA(input: string): GtinCheck {
  const raw = input.replace(/\s+/g, "");
  if (raw.length === 0) return { ok: false, reason: "Enter a UPC code." };
  if (!/^\d+$/.test(raw)) return { ok: false, reason: "UPC-A must contain digits only." };
  if (raw.length !== 12) {
    return { ok: false, reason: `UPC-A must be exactly 12 digits — you entered ${raw.length}.` };
  }
  const body = raw.slice(0, 11);
  const given = Number(raw[11]);
  const expected = computeCheckDigit(body);
  if (given !== expected) {
    return {
      ok: false,
      reason: `Check digit mismatch — expected ${expected}, got ${given}.`,
    };
  }
  return { ok: true, normalized: raw, checkDigit: expected };
}

/**
 * UPC-E → UPC-A expansion per GS1.
 * Accepts 6-, 7- or 8-digit UPC-E and returns the equivalent 12-digit UPC-A.
 */
export function expandUpcE(input: string): string | null {
  const raw = input.replace(/\s+/g, "");
  if (!/^\d+$/.test(raw)) return null;
  let numSys = "0";
  let body: string;
  let check: string | null = null;
  if (raw.length === 6) {
    body = raw;
  } else if (raw.length === 7) {
    numSys = raw[0];
    body = raw.slice(1);
  } else if (raw.length === 8) {
    numSys = raw[0];
    body = raw.slice(1, 7);
    check = raw[7];
  } else {
    return null;
  }
  if (numSys !== "0" && numSys !== "1") return null;
  const [m1, m2, m3, m4, m5, last] = body.split("");
  let upcA: string;
  switch (last) {
    case "0":
    case "1":
    case "2":
      upcA = `${numSys}${m1}${m2}${last}0000${m3}${m4}${m5}`;
      break;
    case "3":
      upcA = `${numSys}${m1}${m2}${m3}00000${m4}${m5}`;
      break;
    case "4":
      upcA = `${numSys}${m1}${m2}${m3}${m4}00000${m5}`;
      break;
    default:
      upcA = `${numSys}${m1}${m2}${m3}${m4}${m5}0000${last}`;
  }
  const cd = computeCheckDigit(upcA);
  if (check !== null && Number(check) !== cd) return null;
  return `${upcA}${cd}`;
}

/** GS1 prefix → likely issuing region/notes. Coarse — illustrative only. */
export function upcANumberSystem(upcA: string): { digit: string; description: string } {
  const d = upcA[0];
  const map: Record<string, string> = {
    "0": "Regular UPC codes (most US/Canada products)",
    "1": "Regular UPC codes",
    "2": "Weighted items / in-store use (random-weight produce, meat)",
    "3": "National Drug Code (NDC) / pharmacy",
    "4": "Loyalty cards and in-store use",
    "5": "Coupons (legacy UPC coupon codes)",
    "6": "Regular UPC codes",
    "7": "Regular UPC codes",
    "8": "Regular UPC codes",
    "9": "Regular UPC codes",
  };
  return { digit: d, description: map[d] ?? "Unknown" };
}

/** Validate an EAN-13 (13 digits) using the GS1 mod-10 algorithm. */
export function validateEan13(input: string): GtinCheck {
  const raw = input.replace(/\s+/g, "");
  if (raw.length === 0) return { ok: false, reason: "Enter an EAN-13 code." };
  if (!/^\d+$/.test(raw)) return { ok: false, reason: "EAN-13 must contain digits only." };
  if (raw.length !== 13) {
    return { ok: false, reason: `EAN-13 must be exactly 13 digits — you entered ${raw.length}.` };
  }
  const expected = computeCheckDigit(raw.slice(0, 12));
  const given = Number(raw[12]);
  if (expected !== given) {
    return { ok: false, reason: `Check digit mismatch — expected ${expected}, got ${given}.` };
  }
  return { ok: true, normalized: raw, checkDigit: expected };
}

/** Validate an EAN-8 (8 digits) using the GS1 mod-10 algorithm. */
export function validateEan8(input: string): GtinCheck {
  const raw = input.replace(/\s+/g, "");
  if (raw.length === 0) return { ok: false, reason: "Enter an EAN-8 code." };
  if (!/^\d+$/.test(raw)) return { ok: false, reason: "EAN-8 must contain digits only." };
  if (raw.length !== 8) {
    return { ok: false, reason: `EAN-8 must be exactly 8 digits — you entered ${raw.length}.` };
  }
  const expected = computeCheckDigit(raw.slice(0, 7));
  const given = Number(raw[7]);
  if (expected !== given) {
    return { ok: false, reason: `Check digit mismatch — expected ${expected}, got ${given}.` };
  }
  return { ok: true, normalized: raw, checkDigit: expected };
}

export type GtinKind = "GTIN-8" | "GTIN-12" | "GTIN-13" | "GTIN-14";

export type GtinValidation =
  | {
      ok: true;
      kind: GtinKind;
      normalized: string;
      checkDigit: number;
    }
  | {
      ok: false;
      reason: string;
      cleaned: string;
      expected?: number;
      kind?: GtinKind;
    };

/**
 * Validate any GTIN (8/12/13/14) with the GS1 mod-10 check digit.
 * Cleans whitespace, dashes and ASCII separators before validating.
 */
export function validateGtin(input: string): GtinValidation {
  const cleaned = input.replace(/[\s\-_.]/g, "");
  if (!cleaned) {
    return { ok: false, reason: "Enter a GTIN (8, 12, 13 or 14 digits).", cleaned };
  }
  if (!/^\d+$/.test(cleaned)) {
    return {
      ok: false,
      reason: "GTIN must contain digits only — remove any letters or punctuation.",
      cleaned,
    };
  }
  const lenMap: Record<number, GtinKind> = {
    8: "GTIN-8",
    12: "GTIN-12",
    13: "GTIN-13",
    14: "GTIN-14",
  };
  const kind = lenMap[cleaned.length];
  if (!kind) {
    return {
      ok: false,
      reason: `GTIN must be 8, 12, 13 or 14 digits. You entered ${cleaned.length}.`,
      cleaned,
    };
  }
  const body = cleaned.slice(0, -1);
  const given = Number(cleaned[cleaned.length - 1]);
  const expected = computeCheckDigit(body);
  if (given !== expected) {
    return {
      ok: false,
      reason: `Check digit mismatch for ${kind} — expected ${expected}, got ${given}.`,
      cleaned,
      expected,
      kind,
    };
  }
  return { ok: true, kind, normalized: cleaned, checkDigit: expected };
}

/** Describe what each GTIN length is typically used for. */
export function gtinKindDescription(kind: GtinKind): string {
  switch (kind) {
    case "GTIN-8":
      return "EAN-8 — compact retail code used on small packages where a full EAN-13 won't fit.";
    case "GTIN-12":
      return "UPC-A — 12-digit retail code used in the United States and Canada.";
    case "GTIN-13":
      return "EAN-13 — 13-digit global retail standard used on most packaged goods worldwide.";
    case "GTIN-14":
      return "ITF-14 / GTIN-14 — logistics identifier used on cases, cartons and pallets.";
  }
}

/**
 * Look up the country / region that issued a GS1 prefix for an EAN-13.
 * Source: GS1 prefix list (public). Coarse ranges — the prefix identifies the
 * GS1 Member Organisation, NOT necessarily where the product was made.
 */
export function ean13Prefix(ean13: string): { prefix: string; description: string } {
  const p = Number(ean13.slice(0, 3));
  const ranges: { from: number; to: number; desc: string }[] = [
    { from: 0, to: 19, desc: "United States & Canada (UPC-A)" },
    { from: 20, to: 29, desc: "In-store / restricted distribution" },
    { from: 30, to: 39, desc: "United States (drugs / NDC)" },
    { from: 40, to: 49, desc: "In-store / restricted distribution" },
    { from: 50, to: 59, desc: "Reserved for future use" },
    { from: 60, to: 99, desc: "United States & Canada" },
    { from: 100, to: 139, desc: "United States" },
    { from: 200, to: 299, desc: "In-store / restricted distribution" },
    { from: 300, to: 379, desc: "France & Monaco" },
    { from: 380, to: 380, desc: "Bulgaria" },
    { from: 383, to: 383, desc: "Slovenia" },
    { from: 385, to: 385, desc: "Croatia" },
    { from: 387, to: 387, desc: "Bosnia and Herzegovina" },
    { from: 389, to: 389, desc: "Montenegro" },
    { from: 390, to: 390, desc: "Kosovo" },
    { from: 400, to: 440, desc: "Germany" },
    { from: 450, to: 459, desc: "Japan" },
    { from: 460, to: 469, desc: "Russia" },
    { from: 470, to: 470, desc: "Kyrgyzstan" },
    { from: 471, to: 471, desc: "Taiwan" },
    { from: 474, to: 474, desc: "Estonia" },
    { from: 475, to: 475, desc: "Latvia" },
    { from: 476, to: 476, desc: "Azerbaijan" },
    { from: 477, to: 477, desc: "Lithuania" },
    { from: 478, to: 478, desc: "Uzbekistan" },
    { from: 479, to: 479, desc: "Sri Lanka" },
    { from: 480, to: 480, desc: "Philippines" },
    { from: 481, to: 481, desc: "Belarus" },
    { from: 482, to: 482, desc: "Ukraine" },
    { from: 483, to: 483, desc: "Turkmenistan" },
    { from: 484, to: 484, desc: "Moldova" },
    { from: 485, to: 485, desc: "Armenia" },
    { from: 486, to: 486, desc: "Georgia" },
    { from: 487, to: 487, desc: "Kazakhstan" },
    { from: 488, to: 488, desc: "Tajikistan" },
    { from: 489, to: 489, desc: "Hong Kong" },
    { from: 490, to: 499, desc: "Japan" },
    { from: 500, to: 509, desc: "United Kingdom" },
    { from: 520, to: 521, desc: "Greece" },
    { from: 528, to: 528, desc: "Lebanon" },
    { from: 529, to: 529, desc: "Cyprus" },
    { from: 530, to: 530, desc: "Albania" },
    { from: 531, to: 531, desc: "North Macedonia" },
    { from: 535, to: 535, desc: "Malta" },
    { from: 539, to: 539, desc: "Ireland" },
    { from: 540, to: 549, desc: "Belgium & Luxembourg" },
    { from: 560, to: 560, desc: "Portugal" },
    { from: 569, to: 569, desc: "Iceland" },
    { from: 570, to: 579, desc: "Denmark, Faroe Islands & Greenland" },
    { from: 590, to: 590, desc: "Poland" },
    { from: 594, to: 594, desc: "Romania" },
    { from: 599, to: 599, desc: "Hungary" },
    { from: 600, to: 601, desc: "South Africa" },
    { from: 603, to: 603, desc: "Ghana" },
    { from: 604, to: 604, desc: "Senegal" },
    { from: 608, to: 608, desc: "Bahrain" },
    { from: 609, to: 609, desc: "Mauritius" },
    { from: 611, to: 611, desc: "Morocco" },
    { from: 613, to: 613, desc: "Algeria" },
    { from: 615, to: 615, desc: "Nigeria" },
    { from: 616, to: 616, desc: "Kenya" },
    { from: 618, to: 618, desc: "Côte d'Ivoire" },
    { from: 619, to: 619, desc: "Tunisia" },
    { from: 620, to: 620, desc: "Tanzania" },
    { from: 621, to: 621, desc: "Syria" },
    { from: 622, to: 622, desc: "Egypt" },
    { from: 623, to: 623, desc: "Brunei" },
    { from: 624, to: 624, desc: "Libya" },
    { from: 625, to: 625, desc: "Jordan" },
    { from: 626, to: 626, desc: "Iran" },
    { from: 627, to: 627, desc: "Kuwait" },
    { from: 628, to: 628, desc: "Saudi Arabia" },
    { from: 629, to: 629, desc: "United Arab Emirates" },
    { from: 630, to: 630, desc: "Qatar" },
    { from: 631, to: 631, desc: "Namibia" },
    { from: 640, to: 649, desc: "Finland" },
    { from: 690, to: 699, desc: "China" },
    { from: 700, to: 709, desc: "Norway" },
    { from: 729, to: 729, desc: "Israel" },
    { from: 730, to: 739, desc: "Sweden" },
    { from: 740, to: 740, desc: "Guatemala" },
    { from: 741, to: 741, desc: "El Salvador" },
    { from: 742, to: 742, desc: "Honduras" },
    { from: 743, to: 743, desc: "Nicaragua" },
    { from: 744, to: 744, desc: "Costa Rica" },
    { from: 745, to: 745, desc: "Panama" },
    { from: 746, to: 746, desc: "Dominican Republic" },
    { from: 750, to: 750, desc: "Mexico" },
    { from: 754, to: 755, desc: "Canada" },
    { from: 759, to: 759, desc: "Venezuela" },
    { from: 760, to: 769, desc: "Switzerland & Liechtenstein" },
    { from: 770, to: 771, desc: "Colombia" },
    { from: 773, to: 773, desc: "Uruguay" },
    { from: 775, to: 775, desc: "Peru" },
    { from: 777, to: 777, desc: "Bolivia" },
    { from: 778, to: 779, desc: "Argentina" },
    { from: 780, to: 780, desc: "Chile" },
    { from: 784, to: 784, desc: "Paraguay" },
    { from: 786, to: 786, desc: "Ecuador" },
    { from: 789, to: 790, desc: "Brazil" },
    { from: 800, to: 839, desc: "Italy, San Marino & Vatican City" },
    { from: 840, to: 849, desc: "Spain & Andorra" },
    { from: 850, to: 850, desc: "Cuba" },
    { from: 858, to: 858, desc: "Slovakia" },
    { from: 859, to: 859, desc: "Czech Republic" },
    { from: 860, to: 860, desc: "Serbia" },
    { from: 865, to: 865, desc: "Mongolia" },
    { from: 867, to: 867, desc: "North Korea" },
    { from: 868, to: 869, desc: "Turkey" },
    { from: 870, to: 879, desc: "Netherlands" },
    { from: 880, to: 881, desc: "South Korea" },
    { from: 883, to: 883, desc: "Myanmar" },
    { from: 884, to: 884, desc: "Cambodia" },
    { from: 885, to: 885, desc: "Thailand" },
    { from: 888, to: 888, desc: "Singapore" },
    { from: 890, to: 890, desc: "India" },
    { from: 893, to: 893, desc: "Vietnam" },
    { from: 896, to: 896, desc: "Pakistan" },
    { from: 899, to: 899, desc: "Indonesia" },
    { from: 900, to: 919, desc: "Austria" },
    { from: 930, to: 939, desc: "Australia" },
    { from: 940, to: 949, desc: "New Zealand" },
    { from: 950, to: 950, desc: "GS1 Global Office" },
    { from: 951, to: 951, desc: "EPCglobal" },
    { from: 952, to: 952, desc: "GS1 demonstration prefix" },
    { from: 955, to: 955, desc: "Malaysia" },
    { from: 958, to: 958, desc: "Macau" },
    { from: 960, to: 969, desc: "GS1 Global Office (GTIN-8 allocations)" },
    { from: 977, to: 977, desc: "Serial publications (ISSN)" },
    { from: 978, to: 979, desc: "Books (ISBN)" },
    { from: 980, to: 980, desc: "Refund receipts" },
    { from: 981, to: 984, desc: "GS1 coupon identification" },
    { from: 990, to: 999, desc: "Coupons" },
  ];
  const prefix = ean13.slice(0, 3);
  const match = ranges.find((r) => p >= r.from && p <= r.to);
  return { prefix, description: match?.desc ?? "Unknown / unassigned GS1 prefix" };
}
